import React, { useState, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Modal } from '../../../../shared/ui/Modal';
import { Box, Button, Typography, IconButton } from '@mui/material';
import moment from 'moment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ComboBox } from '../../../../shared/ui/Combobox';
import { DatePicker } from '../../../../shared/ui/DatePicker';
import { SapNumberInput } from '../SapNumberInput';
import {
  Body,
  Header,
  Title,
  HighlightedText,
  Section,
  SectionLabel,
  SectionGrid,
  SapDeliveryWindowAndTriageBox,
  DeliveryWindowRadioButtonBox,
  ResponsibleOperatorText,
} from './styles';
import { Checkbox } from '../../../../shared/ui/Checkbox';
import { MultipleSelect } from '../../../../shared/ui/MultipleSelect';
import { useOrders } from '../../hooks/useOrders';
import { TriageField } from '../triage';
import { Tooltip } from '../../../../shared/ui/Tooltip';
import { UserHelper } from '../../../../services/auth/UserHelper';
import { CurrentUser } from '../../../../types/auth/User';
import { useOrderStore } from '../../store/ordersStore';
import { ErrorModal } from '../../../../shared/feedback/error';
import { InfoModal } from '../../../../shared/ui/Modal/InfoModal';
import { BsFillFileEarmarkPlusFill } from 'react-icons/bs';

type EditOrderModalProps = {
  open: boolean;
  handleClose: () => void;
  order_id?: number;
  refreshOrdersTable: () => void;
};

export const EditOrderModal: React.FC<EditOrderModalProps> = ({ open, handleClose, order_id, refreshOrdersTable }) => {
  const {
    order,
    setOrder,
    errorValidationMessage,
    visibleSelectItems,
    handleChangeStatus,
    handleChangeStatusComplement,
    handleChangeDeliveryOperation,
    handleChangeServiceEvent,
    handleChangeRetreatReason,
    handleCheckDeliveryWindow,
    handleChangeDistributionCenter,

    fetchCurrentOrder,
    getMultiSelectData,
    handleSave,
    handleSaveDeletedCEC,
    handleSaveUploadedCEC,
    handleClickTakeResponsibility,
  } = useOrders({
    refreshOrdersTable,
    handleClose,
    order_id,
  });

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });

  const [currentUser, setCurrentUser] = React.useState<CurrentUser | undefined>();

  React.useEffect(() => {
    setCurrentUser(UserHelper.getCurrentUser());
    getMultiSelectData();
    fetchCurrentOrder(order_id);
  }, [open]);

  const canEditOrders = currentUser?.isAdmin === '1' || currentUser?.isWhiteMartins === '1';

  const [CECdeleted, setCECdeleted] = useState(false);
  const [CECsent, setCECsent] = useState(false);
  React.useEffect(() => {
    if (CECdeleted) {
      handleSaveDeletedCEC();
      setCECdeleted(false);
    }
  }, [CECdeleted]);

  const [showCECErrorModal, setShowCECErrorModal] = useState(false);
  const [showCECSuccessModal, setShowCECSuccessModal] = useState(false);

  const uploadCEC = useOrderStore((state) => state.uploadCEC);
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const uploadCECFile = async () => {
    hiddenFileInput.current?.click();
  };
  const handleChangeCECFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files) {
      const allFiles: { name: string; content: string | ArrayBuffer | null }[] = [];

      const filePromises = Array.from(files).map((file) => {
        const fileName = file.name.split('.')[0];
        const isNumberRegex = /^[0-9]*$/g;

        if (fileName.length !== 8 || !isNumberRegex.test(fileName)) {
          setShowCECErrorModal(true);
          return;
        }

        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = async function (e) {
            const fileContent = e.target?.result;
            if (fileContent !== undefined) {
              allFiles.push({
                name: fileName,
                content: fileContent,
              });
              resolve(fileContent);
            } else {
              reject(new Error('File content is undefined'));
            }
          };

          reader.onerror = (error) => {
            reject(error);
          };

          reader.readAsDataURL(file);
        });
      });

      await Promise.all(filePromises);

      if (allFiles.length !== 0) {
        const currentUser = UserHelper.getCurrentUser();
        if (currentUser) {
          allFiles.forEach(async (file) => {
            await uploadCEC({
              request_number: file.name,
              user: currentUser.username,
              files: [file],
            });
          });

          allFiles.forEach((file) => {
            if (!order.request_number.includes(file.name)) {
              const new_request_number = [...order.request_number];
              if (new_request_number[new_request_number.length - 1] === '') {
                new_request_number[new_request_number.length - 1] = file.name;
              } else {
                new_request_number.push(file.name);
              }
              setOrder({
                ...order,
                request_number: new_request_number,
              });
            }
          });
          await handleSaveUploadedCEC();
          setCECsent(true);
          setShowCECSuccessModal(true);
        } else {
          console.error('Usuário não encontrado.');
        }
      }
    }
  };

  const receiptMethodItems = [
    { label: 'Selecione', value: '' },
    { label: 'Email', value: 'Email' },
    { label: 'Telefone', value: 'Telefone' },
    { label: 'Whatsapp', value: 'Whatsapp' },
  ];

  const entryDatetimeExceedsCreateIn = Boolean(
    order.entry_datetime && order.create_in && order.entry_datetime.isAfter(order.create_in)
  );
  const entryDatetimeErrorMessage = entryDatetimeExceedsCreateIn
    ? 'Não pode ultrapassar a data de inclusão do pedido'
    : errorValidationMessage.entryDatetime;

  const exitDatetimeExceedsToday = Boolean(order.exit_datetime && order.exit_datetime.isAfter(moment()));
  const exitDatetimeErrorMessage = exitDatetimeExceedsToday
    ? 'Não pode ultrapassar o dia atual'
    : errorValidationMessage.exitDatetime;

  return (
    <>
      <Modal open={open} handleClose={handleClose}>
        <Header>
          <Title variant="h4">Edição de Pedido</Title>
          {order.responsible_operator?.cognito_id === currentUser?.username ? (
            <ResponsibleOperatorText variant="body1">
              Você é o atual responsável por esse pedido
            </ResponsibleOperatorText>
          ) : (
            <Button variant="contained" color="primary" onClick={() => handleClickTakeResponsibility()}>
              Assumir responsabilidade
            </Button>
          )}
        </Header>
        <Body>
          <Box>
            <Typography variant="body1">
              <HighlightedText>Pedido:</HighlightedText> {order?.request_number_temp}
            </Typography>
            <Typography variant="body1">
              <HighlightedText>Operador Responsável:</HighlightedText> {order?.responsible_operator?.name ?? '?'}
            </Typography>
            <Typography variant="body1">
              <HighlightedText>Tipo de Pedido:</HighlightedText> {order?.request_type}
            </Typography>
          </Box>
          <Section>
            <SectionLabel>Entrega</SectionLabel>
            <SectionGrid cols={isTabletOrMobile ? 1 : 4}>
              <DatePicker
                label="Estimativa de Entrega"
                handleChange={(date) => setOrder({ ...order, delivery_forecast: date })}
                value={order.delivery_forecast}
              />
              <ComboBox
                label="Status"
                value={order.status_id}
                onChange={handleChangeStatus}
                items={visibleSelectItems.status}
                sx={{ width: '100%' }}
              />
              <ComboBox
                label="Complemento do Status"
                value={order.status_complement_id}
                onChange={handleChangeStatusComplement}
                items={visibleSelectItems.statusComplements}
                errorHelperText={errorValidationMessage.statusComplement}
                sx={{ width: '100%' }}
              />
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
                <ComboBox
                  label="Filial Distribuidora"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 260,
                        overflowY: 'auto',
                      },
                    },
                    MenuListProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        },
                      },
                    },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                    transformOrigin: { vertical: 'top', horizontal: 'center' },
                  }}
                  value={order.distribution_center == '' ? 'none' : order.distribution_center}
                  onChange={handleChangeDistributionCenter}
                  items={visibleSelectItems.distributionCenters}
                  errorHelperText={errorValidationMessage.distributionCenters}
                  disabled={false}
                  sx={{ flex: 1 }}
                />
                <>
                  <input
                    type="file"
                    onChange={handleChangeCECFile}
                    style={{ display: 'none' }}
                    ref={hiddenFileInput}
                    multiple
                  />
                  <Tooltip title={'Fazer upload de comprovante'}>
                    <IconButton>
                      <BsFillFileEarmarkPlusFill
                        style={{
                          fontSize: '22px',
                          color: 'PrimaryTitleGray',
                          display: 'block',
                        }}
                        onClick={uploadCECFile}
                      />
                    </IconButton>
                  </Tooltip>
                </>
              </Box>
            </SectionGrid>
          </Section>

          <Section>
            <SectionLabel>Recebimento</SectionLabel>
            <SectionGrid cols={isTabletOrMobile ? 1 : 3}>
              <ComboBox
                label="Forma de recebimento"
                value={order.receipt_method ?? ''}
                onChange={(e) =>
                  setOrder({
                    ...order,
                    receipt_method: (e.target.value as 'Email' | 'Telefone' | 'Whatsapp' | '') || undefined,
                  })
                }
                items={receiptMethodItems}
                sx={{ width: '100%' }}
              />
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Data/hora de entrada"
                  value={order.entry_datetime ?? null}
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                  maxDateTime={order.create_in}
                  viewRenderers={{
                    hours: null,
                    minutes: null,
                    seconds: null,
                  }}
                  sx={{ width: '100%' }}
                  onChange={(date) => setOrder({ ...order, entry_datetime: date ?? undefined })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      error: Boolean(entryDatetimeErrorMessage),
                      helperText: entryDatetimeErrorMessage,
                    },
                  }}
                />
                <DateTimePicker
                  label="Data/hora de saída"
                  value={order.exit_datetime ?? null}
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                  maxDateTime={moment()}
                  viewRenderers={{
                    hours: null,
                    minutes: null,
                    seconds: null,
                  }}
                  sx={{ width: '100%' }}
                  onChange={(date) => setOrder({ ...order, exit_datetime: date ?? undefined })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      error: Boolean(exitDatetimeErrorMessage),
                      helperText: exitDatetimeErrorMessage,
                    },
                  }}
                />
              </LocalizationProvider>
            </SectionGrid>
          </Section>

          <Section>
            <SectionLabel>Classificação</SectionLabel>
            <SectionGrid cols={isTabletOrMobile ? 1 : 3}>
              <MultipleSelect
                label="Operações de entrega"
                value={order.delivery_operations}
                onChange={handleChangeDeliveryOperation}
                items={visibleSelectItems.deliveryOperations}
                sx={{ width: '100%' }}
              />

              <MultipleSelect
                label="Eventos do atendimento"
                value={order.service_events}
                onChange={handleChangeServiceEvent}
                items={visibleSelectItems.serviceEvents}
                sx={{ width: '100%' }}
              />

              <Tooltip
                title={
                  order?.request_type !== 'Recolhimento'
                    ? 'Opção disponível apenas para pedidos de recolhimento'
                    : undefined
                }
                sx={{ width: '100%' }}
              >
                <ComboBox
                  label="Motivos do recolhimento"
                  value={order.retreat_reasons}
                  onChange={handleChangeRetreatReason}
                  items={visibleSelectItems.retreatReasons}
                  errorHelperText={errorValidationMessage.retreatReason}
                />
              </Tooltip>
            </SectionGrid>
          </Section>

          <SapDeliveryWindowAndTriageBox>
            <DeliveryWindowRadioButtonBox>
              <Checkbox
                row
                items={order.delivery_window}
                handleCheck={handleCheckDeliveryWindow}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '15px',
                }}
              />
              <SapNumberInput
                numbers={order.request_number}
                setNumbers={(newNumbers: string[]) => setOrder({ ...order, request_number: newNumbers })}
                setCECsent={setCECsent}
                CECsent={CECsent}
                setCECdeleted={setCECdeleted}
                CECdeleted={CECdeleted}
              />
            </DeliveryWindowRadioButtonBox>
            {currentUser?.isWhiteMartins || currentUser?.isAdmin ? (
              <Box sx={{ width: '78%' }}>
                <TriageField orderId={order_id} inputField={true} showTitle={true} open={open}></TriageField>
              </Box>
            ) : (
              <></>
            )}
          </SapDeliveryWindowAndTriageBox>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canEditOrders || entryDatetimeExceedsCreateIn || exitDatetimeExceedsToday}
            >
              Salvar
            </Button>
          </Box>
        </Body>
      </Modal>
      <ErrorModal
        title={''}
        showErrorModal={showCECErrorModal}
        setShowErrorModal={setShowCECErrorModal}
        message="O nome do arquivo deve possuir apenas números e deve ter 8 dígitos. Certifique-se que o número SAP foi adicionado ao pedido."
      />
      <InfoModal
        open={showCECSuccessModal}
        handleClose={() => setShowCECSuccessModal(false)}
        message="Upload de CEC realizado com sucesso!"
      />
    </>
  );
};
